// Library Utilities
import React, { useState } from "react";
import moment from "moment";

// components
import Button from "../../../../Shared/button/button";

// Constants
import { MEDIUM } from "../../../../Shared/buttonSize";
import PauseSvg from "../../../../assets/images/stop-icon";
import { ReactComponent as CopySvg } from "../../../../assets/images/copy.svg";

// Styles
import styles from "./trackTask.module.scss";
import {
  disabledButtonStyle,
  borderButtonStyle,
  blueHoverButtonStyle,
} from "../../../../Shared/buttonStyles";
import { testIdProps } from "../../../../utils/testIdProps";
import { ArrowDownToLine } from "lucide-react";
import Toast from "../../../../Shared/toast/toast";
import { triggerBrowserDownload } from "../batchDownload";
import {
  customerBatchDisplayState,
  isCustomerBatchFinished,
} from "../normalizeBatchTask";

interface responseCodeCount {
  response_code?: string;
  count?: any;
  billable?: "Y" | "N";
}

interface TrackDataObject {
  batchId: string;
  batchName: string | null;
  batch_state?: string;
  batchState: string;
  batchType: string;
  created_at: string;
  destinationMode: string;
  downloadInitiated: boolean;
  downloadAvailable?: boolean;
  downloadUrl: string | null;
  orgId: string;
  orgName: string | null;
  totalItems: number;
  totalProcessed: number;
  tpm: any;
  updated_at: string;
  uploadSource: string | null;
  userEmail: string | null;
  successCount: responseCodeCount[];
}

interface ProgressData {
  item: TrackDataObject;
  executeAction: Function;
  btndisable: boolean;
  loading: any;
}

const ProgressCard = ({
  item,
  btndisable: _btnDisable,
  loading,
  executeAction,
}: ProgressData) => {
  const [copiedText, setCopiedText] = useState<Boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const displayState = customerBatchDisplayState(item as any);
  const canDownload = isCustomerBatchFinished(item as any);

  const useDynamicHover = () => {
    const [hoveredItems, setHoveredItems] = useState<{
      [key: string]: boolean;
    }>({});

    const handleMouseEnter = (key: string) => {
      setHoveredItems((prev: any) => ({ ...prev, [key]: true }));
    };

    const handleMouseLeave = (key: string) => {
      setHoveredItems((prev: any) => ({ ...prev, [key]: false }));
    };

    return { hoveredItems, handleMouseEnter, handleMouseLeave };
  };

  const { hoveredItems, handleMouseEnter, handleMouseLeave } =
    useDynamicHover();

  // To get the percentage of task
  function percentage(partialValue: number, totalValue: number) {
    const percent = (100 * partialValue) / totalValue;
    return percent.toFixed(2);
  }
  const waitingForApproval = () => {
    return (
      <div className={styles.waitingForApproval}>Waiting For Approval </div>
    );
  };

  async function copyBatchId(batchId: string) {
    try {
      if (batchId) {
        await navigator.clipboard.writeText(batchId);
        setCopiedText(true);
      }
      // setCopiedText(true);
    } catch (error) {
      console.error(error);
    }
  }

  async function handleDownload() {
    if (!canDownload || downloadLoading) return;
    const existingUrl = String(item?.downloadUrl || "").trim();
    if (existingUrl) {
      triggerBrowserDownload(existingUrl);
      return;
    }
    setDownloadLoading(true);
    try {
      await executeAction(item.batchId, "GENERATE_DOWNLOAD_LINK");
    } catch (error: any) {
      Toast(
        "error",
        error?.message || "Failed to download batch results",
        "Error"
      );
    } finally {
      setDownloadLoading(false);
    }
  }

  const stopBtn = (data: any) => {
    const batchPercentage = percentage(
      data?.total_completed || 0,
      data?.total_items || 0
    );
    const disable =
      Number(batchPercentage) > 90 &&
      (String(data.status ?? "").toUpperCase() === "RUNNING" ||
        String(data.status ?? "").toUpperCase() === "PAUSED");

    return (
      <div
        className={`${styles.actionButton} ${styles.stopBtn}
           ${!disable ? styles.disabled : ""}
          `}
        id="batch-service-stop-task"
        onClick={() => disable && executeAction(data, "STOP_TASK")}
      >
        <div
          className="d-flex align-items-center h-100 justify-content-center"
          id="progress-card-pause-task"
          onMouseEnter={() => handleMouseEnter("isPause")}
          onMouseLeave={() => handleMouseLeave("isPause")}
        >
          <PauseSvg
            fillBackgroundColor={
              hoveredItems["isPause"]
                ? "#DB4437"
                : !disable
                  ? "#DDDCDD"
                  : "#FFFFFF"
            }
            InnerColor={
              hoveredItems["isPause"]
                ? "#FFFFFF"
                : !disable
                  ? "#3b3b3b"
                  : "#DB4437"
            }
            border={
              hoveredItems["isPause"]
                ? "#FFFFFF"
                : !disable
                  ? "#fffff"
                  : "#DB4437"
            }
          />
        </div>
      </div>
    );
  };

  return (
    <tr className={styles.progressCardRow}>
      <td
        style={{
          borderLeft:
            displayState === "ERROR"
              ? "6px solid #db4437"
              : displayState === "FINISHED"
                ? "6px solid #0f9d58"
                : displayState === "PROGRESS"
                  ? "6px solid #7F00FF"
                  : "6px solid #ffc922",
        }}
      >
        <div className={styles.progressBlock + " pl-1"}>
          <div className={styles.batchName}>{item.batchName}</div>

          <div className="d-flex gap-2">
            <div className="mr-2">ID: </div>
            <div className={styles.batchIdValue}>{item?.batchId}</div>
            <p
              className={styles.copyButton}
              style={{
                height: "30px",
                cursor: "pointer",
              }}
            >
              <CopySvg
                id="progress-card-copy-batchId"
                onClick={() => copyBatchId(item.batchId)}
              />
              <p className={styles.copyText}>
                {copiedText ? "Copied!" : "Copy text"}
              </p>
            </p>
          </div>

          <div className="d-flex gap-2">
            <div className="mr-2">ARN: </div>
            <div className={styles.value}>{item?.batchType}</div>
          </div>
          <div className="d-flex gap-2">
            <div className="mr-2">Created at: </div>
            <div className={styles.value}>
              {/* <strong className={trackTaskStyle.value}> */}
              {moment(item?.created_at).format("DD/MM/YYYY, HH:mm:ss")}
              {/* </strong> */}
            </div>
          </div>
          <div className="d-flex gap-2">
            <div className="mr-2">Updated At: </div>
            <div className={styles.value}>
              {moment(item?.updated_at).format("DD/MM/YYYY, HH:mm:ss")}
            </div>
          </div>
        </div>
      </td>
      <td>
        {" "}
        <div className={styles.borderGap} />
      </td>
      <td className={styles.progressColumn}>
        <div
          className={displayState === "PAUSED" ? styles.progressDisable : ""}
        >
          {displayState === "Waiting for approval" ? (
            waitingForApproval()
          ) : (
            <>
              <div className={styles.progressHead}>Progress:</div>
              <progress
                id="file"
                value={item?.totalProcessed}
                max={item?.totalItems}
                style={{ width: "75%", paddingRight: "10px" }}
              />
              <span className="">
                {percentage(item?.totalProcessed || 0, item?.totalItems || 0)}%
              </span>

              <div>
                {displayState === "ERROR" ? (
                  <>
                    <div className={styles.errorBox}>ERROR</div>
                  </>
                ) : displayState === "FINISHED" ? (
                  <div className={styles.finishedBox}>FINISHED</div>
                ) : displayState === "PROGRESS" ? (
                  <div className={styles.progressBox}>PROGRESS</div>
                ) : (
                  <div className={styles.pausedBox}>{displayState}</div>
                )}
              </div>
            </>
          )}
        </div>
      </td>
      <td>
        <div className={styles.borderGap} />
      </td>
      <td>
        <div>{stopBtn(item.batchId)}</div>
      </td>
      <td>
        <div className="w-50" {...testIdProps("batch-service-download")}>
          <Button
            id={
              canDownload
                ? "batch-service-download-button"
                : "batch-service-download-disabled"
            }
            size={MEDIUM}
            hoveredStyle={
              canDownload && !downloadLoading
                ? blueHoverButtonStyle
                : disabledButtonStyle
            }
            style={
              canDownload && !downloadLoading
                ? borderButtonStyle
                : disabledButtonStyle
            }
            isLoading={downloadLoading || Boolean(loading)}
            disabled={!canDownload || downloadLoading}
            className={
              styles.downloadBtn + " " + (canDownload ? "" : styles.disableBtn)
            }
            onMouseEnter={() => handleMouseEnter("isDownload")}
            onMouseLeave={() => handleMouseLeave("isDownload")}
            onClick={handleDownload}
          >
            <span className="mr-2">Download</span>
            <ArrowDownToLine
              className={canDownload ? styles.iconHover : undefined}
              color={canDownload ? undefined : "#969696"}
              size={16}
            />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default ProgressCard;
